import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getUsers } from '@/services/api/users';
import { getMetaPages } from '@/services/api/connections';
import {
    getUserPageAccesses,
    grantTotalBulkAccess,
    revokeAccess,
    type PageAccessWithPage,
} from '@/services/api/page-access';
import { getCurrentUser } from '@/services/api/auth';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import {
    RefreshCw, AlertCircle, Shield, Users, Search,
    ChevronDown, Save, UserCheck, X
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { User } from '@/types/user';
import type { MetaPage } from '@/pages/Meta/Connection/types/meta.types';

interface PagePermissions {
    page_id: number;
    enabled: boolean;
}

export default function PageAccess() {
    const currentUser = getCurrentUser();
    const isAdmin = currentUser?.role === 'admin';
    const isParent = !isAdmin && !currentUser?.parent_id;
    const canManage = isAdmin || isParent;

    const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
    const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
    const [userSearch, setUserSearch] = useState('');
    const [pageSearch, setPageSearch] = useState('');
    const [permissions, setPermissions] = useState<Map<number, PagePermissions>>(new Map());
    const [hasChanges, setHasChanges] = useState(false);

    const { toast } = useToast();
    const queryClient = useQueryClient();

    // Fetch users: admin vê todos, pai vê só filhos
    const { data: allUsers = [], isLoading: loadingUsers } = useQuery({
        queryKey: ['users'],
        queryFn: getUsers,
        enabled: canManage,
    });

    // Filtra: admin vê todos (menos admins), pai vê só seus filhos
    const users = allUsers.filter((u: User) => {
        if (isAdmin) return u.role !== 'admin';
        if (isParent) return u.parent_id === currentUser?.id;
        return false;
    });

    // Fetch pages: admin vê todas, pai vê as suas
    const { data: pages = [], isLoading: loadingPages } = useQuery({
        queryKey: ['meta-pages'],
        queryFn: getMetaPages,
        enabled: canManage,
    });

    // Fetch selected user's current accesses
    const { data: userAccesses = [], isLoading: loadingAccesses } = useQuery({
        queryKey: ['page-access', selectedUserId],
        queryFn: () => getUserPageAccesses(selectedUserId!),
        enabled: !!selectedUserId,
    });

    // Initialize permissions when user is selected or accesses load
    useEffect(() => {
        if (!selectedUserId || !pages.length) return;

        const newPerms = new Map<number, PagePermissions>();
        const accessMap = new Map<number, PageAccessWithPage>();
        userAccesses.forEach(a => accessMap.set(a.page_id, a));

        pages.forEach((page: MetaPage) => {
            const existing = accessMap.get(page.id);
            newPerms.set(page.id, {
                page_id: page.id,
                enabled: Boolean(existing?.can_view || existing?.can_edit || existing?.can_broadcast),
            });
        });

        setPermissions(newPerms);
        setHasChanges(false);
    }, [selectedUserId, pages, userAccesses]);

    // Save mutation
    const saveMutation = useMutation({
        mutationFn: async () => {
            if (!selectedUserId) return;

            const toGrant: number[] = [];
            const toRevoke: number[] = [];
            const hasFullAccess = (access?: PageAccessWithPage) =>
                Boolean(access?.can_access || (access?.can_view && access?.can_edit && access?.can_broadcast));

            permissions.forEach((perm, pageId) => {
                const existing = userAccesses.find(a => a.page_id === pageId);

                if (perm.enabled) {
                    if (!hasFullAccess(existing)) {
                        toGrant.push(pageId);
                    }
                } else if (existing) {
                    toRevoke.push(pageId);
                }
            });

            // Revoke removed accesses
            for (const pageId of toRevoke) {
                await revokeAccess(selectedUserId, pageId);
            }

            // Grant/update accesses
            if (toGrant.length > 0) {
                await grantTotalBulkAccess(selectedUserId, toGrant);
            }
        },
        onSuccess: () => {
            toast({ title: 'Acessos salvos!', description: 'Permissões atualizadas com sucesso.' });
            queryClient.invalidateQueries({ queryKey: ['page-access', selectedUserId] });
            setHasChanges(false);
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao salvar',
                description: error.response?.data?.detail || 'Falha ao atualizar permissões.',
            });
        },
    });

    const togglePermission = (pageId: number) => {
        setPermissions(prev => {
            const next = new Map(prev);
            const perm = { ...next.get(pageId)! };
            perm.enabled = !perm.enabled;
            next.set(pageId, perm);
            return next;
        });
        setHasChanges(true);
    };

    const selectAllView = () => {
        setPermissions(prev => {
            const next = new Map(prev);
            const allEnabled = [...next.values()].every(p => p.enabled);
            next.forEach((perm, pageId) => {
                next.set(pageId, { ...perm, enabled: !allEnabled });
            });
            return next;
        });
        setHasChanges(true);
    };

    const selectedUser = users.find((u: User) => u.id === selectedUserId);
    const filteredUsers = users.filter((u: User) =>
        u.name.toLowerCase().includes(userSearch.toLowerCase()) ||
        u.email.toLowerCase().includes(userSearch.toLowerCase())
    );
    const filteredPages = pages.filter((p: MetaPage) =>
        p.page_name.toLowerCase().includes(pageSearch.toLowerCase())
    );

    const accessCount = [...permissions.values()].filter(p => p.enabled).length;

    if (!canManage) {
        return (
            <div className="flex flex-col items-center justify-center p-20 text-gray-400">
                <Shield className="h-16 w-16 mb-4" />
                <h2 className="text-xl font-bold text-gray-600">Acesso Restrito</h2>
                <p className="mt-2">Apenas administradores e usuários principais podem gerenciar acessos.</p>
            </div>
        );
    }

    return (
        <div className="space-y-6 pb-10">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900 font-outfit tracking-tight">Gerenciar Acessos</h1>
                    <p className="mt-1 text-gray-500 text-lg">Libere ou remova o acesso das páginas para cada usuário.</p>
                </div>
                {hasChanges && selectedUserId && (
                    <Button
                        onClick={() => saveMutation.mutate()}
                        disabled={saveMutation.isPending}
                        className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold shadow-lg shadow-blue-200 gap-2"
                    >
                        {saveMutation.isPending ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                        Salvar Acessos
                    </Button>
                )}
            </div>

            {/* User Selector */}
            <div className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm">
                <div className="flex items-center gap-2 mb-4">
                    <Users className="h-5 w-5 text-gray-400" />
                    <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-500">Selecionar Usuário</h2>
                </div>

                <div className="relative">
                    <button
                        onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
                        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl hover:bg-white hover:border-blue-200 transition-all text-left"
                    >
                        {selectedUser ? (
                            <div className="flex items-center gap-3">
                                <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                                    <span className="text-sm font-bold text-blue-600">
                                        {selectedUser.name.charAt(0).toUpperCase()}
                                    </span>
                                </div>
                                <div>
                                    <p className="font-medium text-gray-900 text-sm">{selectedUser.name}</p>
                                    <p className="text-xs text-gray-400">{selectedUser.email}</p>
                                </div>
                                <Badge variant="secondary" className="ml-2 text-[10px]">{selectedUser.role}</Badge>
                            </div>
                        ) : (
                            <span className="text-gray-400">Selecione um usuário...</span>
                        )}
                        <ChevronDown className={cn("h-4 w-4 text-gray-400 transition-transform", isUserDropdownOpen && "rotate-180")} />
                    </button>

                    {isUserDropdownOpen && (
                        <div className="absolute z-50 w-full mt-2 bg-white border border-gray-100 rounded-xl shadow-xl overflow-hidden">
                            <div className="p-2 border-b border-gray-50">
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder="Buscar usuário..."
                                        value={userSearch}
                                        onChange={(e) => setUserSearch(e.target.value)}
                                        className="w-full pl-9 pr-4 py-2 bg-gray-50 border-none rounded-lg text-sm focus:ring-1 focus:ring-blue-100 outline-none"
                                        autoFocus
                                    />
                                </div>
                            </div>
                            <div className="max-h-60 overflow-y-auto">
                                {loadingUsers ? (
                                    <div className="p-4 text-center text-gray-400 text-sm">Carregando...</div>
                                ) : filteredUsers.length === 0 ? (
                                    <div className="p-4 text-center text-gray-400 text-sm">Nenhum usuário encontrado</div>
                                ) : (
                                    filteredUsers.map((user: User) => (
                                        <button
                                            key={user.id}
                                            onClick={() => {
                                                setSelectedUserId(user.id);
                                                setIsUserDropdownOpen(false);
                                                setUserSearch('');
                                            }}
                                            className={cn(
                                                "w-full flex items-center gap-3 px-4 py-3 hover:bg-blue-50 transition-colors text-left",
                                                selectedUserId === user.id && "bg-blue-50"
                                            )}
                                        >
                                            <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center">
                                                <span className="text-sm font-bold text-gray-500">
                                                    {user.name.charAt(0).toUpperCase()}
                                                </span>
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="font-medium text-gray-900 text-sm truncate">{user.name}</p>
                                                <p className="text-xs text-gray-400 truncate">{user.email}</p>
                                            </div>
                                            <Badge variant="secondary" className="text-[10px] shrink-0">{user.role}</Badge>
                                        </button>
                                    ))
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Pages Table */}
            {selectedUserId && (
                <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
                    {/* Table Header */}
                    <div className="px-6 py-4 border-b border-gray-50 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <Shield className="h-5 w-5 text-blue-500" />
                            <h2 className="font-semibold text-gray-900">
                                Páginas
                                <span className="ml-2 text-sm font-normal text-gray-400">
                                    {accessCount} de {pages.length} liberadas
                                </span>
                            </h2>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Buscar página..."
                                    value={pageSearch}
                                    onChange={(e) => setPageSearch(e.target.value)}
                                    className="pl-9 pr-4 py-2 bg-gray-50 border border-gray-100 rounded-lg text-sm focus:ring-1 focus:ring-blue-100 outline-none w-64"
                                />
                            </div>
                            <Button variant="outline" size="sm" className="rounded-lg text-xs gap-1.5" onClick={selectAllView}>
                                <UserCheck className="h-3.5 w-3.5" />
                                Alternar Todos
                            </Button>
                        </div>
                    </div>

                    {loadingPages || loadingAccesses ? (
                        <div className="flex items-center justify-center p-16">
                            <RefreshCw className="h-6 w-6 animate-spin text-blue-600" />
                        </div>
                    ) : filteredPages.length === 0 ? (
                        <div className="flex flex-col items-center justify-center p-16 text-gray-400">
                            <AlertCircle className="h-8 w-8 mb-2" />
                            <p className="text-sm">Nenhuma página encontrada</p>
                        </div>
                    ) : (
                        <div>
                            {/* Column Headers */}
                            <div className="grid grid-cols-[1fr,120px] px-6 py-3 bg-gray-50/50 border-b border-gray-50 text-[10px] font-bold uppercase tracking-wider text-gray-400">
                                <span>Página</span>
                                <span className="text-center">Liberado</span>
                            </div>

                            {/* Rows */}
                            {filteredPages.map((page: MetaPage) => {
                                const perm = permissions.get(page.id);
                                if (!perm) return null;

                                return (
                                    <div
                                        key={page.id}
                                        className={cn(
                                            "grid grid-cols-[1fr,120px] px-6 py-4 border-b border-gray-50 items-center hover:bg-gray-50/50 transition-colors",
                                            perm.enabled && "bg-blue-50/30"
                                        )}
                                    >
                                        <div className="flex items-center gap-3 min-w-0">
                                            <div className="h-10 w-10 rounded-xl bg-gray-100 flex items-center justify-center text-gray-400 text-sm font-bold">
                                                {page.page_name?.charAt(0)?.toUpperCase()}
                                            </div>
                                            <div className="min-w-0">
                                                <p className="font-medium text-gray-900 text-sm truncate">{page.page_name}</p>
                                                <p className="text-xs text-gray-400 truncate">ID: {page.page_id}</p>
                                            </div>
                                            {page.is_active ? (
                                                <Badge variant="secondary" className="text-[10px] bg-green-50 text-green-600 border-green-100 shrink-0">Ativa</Badge>
                                            ) : (
                                                <Badge variant="secondary" className="text-[10px] bg-red-50 text-red-500 border-red-100 shrink-0">Inativa</Badge>
                                            )}
                                        </div>

                                        <div className="flex justify-center">
                                            <Switch
                                                checked={perm.enabled}
                                                onCheckedChange={() => togglePermission(page.id)}
                                                className="data-[state=checked]:bg-[#111827]"
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* Empty State */}
            {!selectedUserId && (
                <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                    <Users className="h-16 w-16 mb-4 text-gray-200" />
                    <h3 className="text-lg font-semibold text-gray-500">Selecione um usuário</h3>
                    <p className="text-sm mt-1">Escolha um usuário acima para gerenciar seus acessos às páginas.</p>
                </div>
            )}

            {/* Floating Save Button */}
            {hasChanges && selectedUserId && (
                <div className="fixed bottom-6 right-6 z-50">
                    <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-2xl shadow-2xl px-4 py-3">
                        <span className="text-sm text-gray-500">Alterações não salvas</span>
                        <Button
                            variant="ghost"
                            size="sm"
                            className="rounded-lg"
                            onClick={() => {
                                queryClient.invalidateQueries({ queryKey: ['page-access', selectedUserId] });
                                setHasChanges(false);
                            }}
                        >
                            <X className="h-4 w-4" />
                        </Button>
                        <Button
                            onClick={() => saveMutation.mutate()}
                            disabled={saveMutation.isPending}
                            size="sm"
                            className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold gap-2"
                        >
                            {saveMutation.isPending ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                            Salvar
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}
